// =====================================================================
// Faz T (2026-05-06) — Customer Journey Map
// =====================================================================
// Owner için "ziyaretçi siteye geldi, ne yaptı, ne için ayrıldı" sorusunu
// anlamlı bir görsele çevirir.
//
// Sayfa 2 panel:
//   SOL: Top N session listesi (filter: tümü / dönüşmüş / sepet terk eden)
//        - her satırda: zaman, kullanıcı, entry → exit, sayfa sayısı, süre,
//          dönüşmüş mü (✓ sipariş ikonu)
//   SAĞ: Seçili session'ın timeline'ı
//        - sırayla her sayfa: ne kadar kaldı, hangi kaynaktan geldi,
//          sonunda sipariş olduysa kart
//
// Endpoint'ler (Faz T API):
//   GET /api/analytics/journeys?days=N&limit=N&filter=...
//   GET /api/analytics/journey/:sessionId
// =====================================================================
import React, { useEffect, useState, useMemo } from "react";
import {
  Box, Card, Typography, Grid, Chip, CircularProgress, Alert, IconButton,
  Tooltip, ToggleButton, ToggleButtonGroup, List, ListItem, ListItemButton,
  Divider, Avatar
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import LanguageIcon from "@mui/icons-material/Language";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import API from "../services/api";

// ──────────────────────────────────────────────────────────────────────
// Yardımcı formatter'lar — sayfa içinde tekrar tekrar kullanılıyor
// ──────────────────────────────────────────────────────────────────────

// Saniye → "Xs / X dk / X sa / X g" formatına çevir
const fmtDuration = (sec) => {
  if (sec == null) return "—";
  if (sec < 60) return `${sec}sn`;
  if (sec < 3600) return `${Math.floor(sec / 60)}dk`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}sa`;
  return `${Math.floor(sec / 86400)}g`;
};

// "az önce / X dk önce / 12:34" şeklinde göster
const timeAgo = (date) => {
  const d = new Date(date);
  const sec = Math.floor((Date.now() - d) / 1000);
  if (sec < 60) return "az önce";
  if (sec < 3600) return `${Math.floor(sec / 60)}dk önce`;
  if (sec < 86400) return d.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });
  return d.toLocaleString("tr-TR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
};

// URL'yi okunur sayfa adına çevir — "/urun/abc-xyz" → "Ürün: abc-xyz"
// Frontend'de tekrar yazıldı (Site Trafiği'ndeki ile aynı) — minik bir
// kod tekrarı ama her sayfa kendi formatını esnek tutsun diye lokal kaldı
const pageLabel = (page) => {
  if (!page) return "—";
  if (page === "/") return "Anasayfa";
  if (page.startsWith("/urun/")) return "Ürün: " + page.replace("/urun/", "").slice(0, 30);
  if (page.startsWith("/kategori/")) return "Kat: " + page.replace("/kategori/", "").slice(0, 30);
  if (page.includes("/sepet")) return "🛒 Sepet";
  if (page.includes("/checkout") || page.includes("/odeme")) return "💳 Ödeme";
  if (page.includes("/urunler")) return "Tüm Ürünler";
  if (page.includes("/profilim")) return "Profil";
  if (page.includes("/giris") || page.includes("/login")) return "Giriş";
  if (page.includes("/uye-ol") || page.includes("/register")) return "Kayıt";
  return page.length > 35 ? page.slice(0, 35) + "..." : page;
};

// Para — TL formatı
const fmtMoney = (v) => Number(v || 0).toLocaleString("tr-TR", { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + " ₺";

// ──────────────────────────────────────────────────────────────────────
// Ana bileşen
// ──────────────────────────────────────────────────────────────────────
export default function CustomerJourney() {
  // Session listesi state'leri
  const [sessions, setSessions] = useState([]);
  const [filter, setFilter] = useState("all");          // all / converted / abandoned_cart
  const [days, setDays] = useState(7);                   // periyot
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  // Detay panel state'leri
  const [selectedSession, setSelectedSession] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // ──────────────────────────────────────────────────────────────────
  // Session listesini yükle (filter veya days değişince yeniden çekilir)
  // ──────────────────────────────────────────────────────────────────
  const loadSessions = () => {
    setLoading(true);
    API.get(`/api/analytics/journeys?days=${days}&limit=80&filter=${filter}`)
      .then(r => { setSessions(r.data.sessions || []); setErr(null); })
      .catch(e => setErr(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  };
  useEffect(loadSessions, [days, filter]);

  // ──────────────────────────────────────────────────────────────────
  // Session seçilince detay (timeline) çek
  // selectedSession değişince tetiklenir
  // ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!selectedSession) { setDetail(null); return; }
    setDetailLoading(true);
    API.get(`/api/analytics/journey/${encodeURIComponent(selectedSession)}`)
      .then(r => setDetail(r.data))
      .catch(e => setDetail({ error: e.response?.data?.error || e.message }))
      .finally(() => setDetailLoading(false));
  }, [selectedSession]);

  // ──────────────────────────────────────────────────────────────────
  // Filtre KPI'ları (count'lar) — kullanıcıya filter butonlarındaki
  // sayıları göstersin diye memo'la (sadece sessions değişince hesapla)
  // ──────────────────────────────────────────────────────────────────
  const counts = useMemo(() => {
    const c = { all: sessions.length, converted: 0, abandoned_cart: 0 };
    for (const s of sessions) {
      if (s.has_order) c.converted++;
      if (s.has_cart_view && !s.has_order) c.abandoned_cart++;
    }
    return c;
  }, [sessions]);

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      {/* Üst bar — başlık + filter + yenile */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3, flexWrap: "wrap", gap: 1 }}>
        <Box>
          <Typography variant="h4" fontWeight={800}>Customer Journey</Typography>
          <Typography variant="body2" color="text.secondary">
            Ziyaretçi yolculuğu — kim hangi sayfayı sırasıyla gezdi
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <ToggleButtonGroup size="small" value={days} exclusive onChange={(_, v) => v && setDays(v)}>
            <ToggleButton value={1}>1g</ToggleButton>
            <ToggleButton value={7}>7g</ToggleButton>
            <ToggleButton value={30}>30g</ToggleButton>
          </ToggleButtonGroup>
          <Tooltip title="Yenile">
            <IconButton onClick={loadSessions}><RefreshIcon /></IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Filter chip'leri — tıklayınca aktif filter değişir */}
      <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
        <Chip
          label={`Tümü (${counts.all})`}
          onClick={() => setFilter("all")}
          color={filter === "all" ? "primary" : "default"}
          variant={filter === "all" ? "filled" : "outlined"}
        />
        <Chip
          label={`✓ Dönüşmüş (${counts.converted})`}
          onClick={() => setFilter("converted")}
          sx={{
            bgcolor: filter === "converted" ? "#10b981" : "transparent",
            color: filter === "converted" ? "white" : "#10b981",
            border: "1px solid #10b981",
            fontWeight: 600
          }}
        />
        <Chip
          label={`🛒 Sepet Terk (${counts.abandoned_cart})`}
          onClick={() => setFilter("abandoned_cart")}
          sx={{
            bgcolor: filter === "abandoned_cart" ? "#f59e0b" : "transparent",
            color: filter === "abandoned_cart" ? "white" : "#f59e0b",
            border: "1px solid #f59e0b",
            fontWeight: 600
          }}
        />
      </Box>

      {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}

      <Grid container spacing={2}>
        {/* SOL: Session listesi */}
        <Grid item xs={12} md={5}>
          <Card sx={{ borderRadius: 3, overflow: "hidden", maxHeight: "75vh" }}>
            <Box sx={{ p: 2, bgcolor: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
              <Typography variant="h6" fontWeight={700}>
                Session'lar
                <Chip size="small" label={sessions.length} sx={{ ml: 1 }} />
              </Typography>
            </Box>
            {loading ? (
              <Box sx={{ p: 4, textAlign: "center" }}><CircularProgress size={24} /></Box>
            ) : sessions.length === 0 ? (
              <Box sx={{ p: 3 }}>
                <Typography color="text.secondary" fontSize={14}>
                  Bu kriterde session yok.
                </Typography>
              </Box>
            ) : (
              <List dense disablePadding sx={{ overflow: "auto", maxHeight: "70vh" }}>
                {sessions.map((s, i) => (
                  <React.Fragment key={s.session_id}>
                    <ListItem disablePadding>
                      <ListItemButton
                        selected={selectedSession === s.session_id}
                        onClick={() => setSelectedSession(s.session_id)}
                        sx={{
                          py: 1.2, px: 2,
                          borderLeft: s.has_order ? "3px solid #10b981"
                                    : s.has_cart_view ? "3px solid #f59e0b"
                                    : "3px solid transparent"
                        }}
                      >
                        <Box sx={{ width: "100%" }}>
                          {/* Üst satır: ikon + kullanıcı + zaman */}
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.3 }}>
                            {s.source === "mobile"
                              ? <PhoneIphoneIcon sx={{ color: "#10b981", fontSize: 16 }} />
                              : <LanguageIcon sx={{ color: "#3b82f6", fontSize: 16 }} />}
                            <Typography fontWeight={600} fontSize={13} noWrap sx={{ flex: 1 }}>
                              {s.company_name || s.user_name || (
                                <span style={{ color: "#94a3b8", fontFamily: "monospace", fontSize: 11 }}>
                                  {s.ip || "anonim"}
                                </span>
                              )}
                            </Typography>
                            {s.has_order && (
                              <Tooltip title="Bu session'da sipariş tamamlandı">
                                <CheckCircleIcon sx={{ color: "#10b981", fontSize: 16 }} />
                              </Tooltip>
                            )}
                            <Typography variant="caption" color="text.disabled" fontSize={10}>
                              {timeAgo(s.first_seen)}
                            </Typography>
                          </Box>
                          {/* Alt satır: entry → exit page */}
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, fontSize: 11, color: "text.secondary" }}>
                            <Typography variant="caption" sx={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {pageLabel(s.entry_page)}
                            </Typography>
                            <ArrowForwardIcon sx={{ fontSize: 12, color: "#cbd5e1" }} />
                            <Typography variant="caption" sx={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {pageLabel(s.exit_page)}
                            </Typography>
                          </Box>
                          {/* En alt: page count + duration */}
                          <Box sx={{ display: "flex", gap: 1, mt: 0.3 }}>
                            <Chip size="small" label={`${s.page_count} sayfa`} sx={{ height: 18, fontSize: 10 }} />
                            <Chip size="small" label={fmtDuration(s.duration_minutes * 60)} sx={{ height: 18, fontSize: 10 }} />
                          </Box>
                        </Box>
                      </ListItemButton>
                    </ListItem>
                    {i < sessions.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            )}
          </Card>
        </Grid>

        {/* SAĞ: Detay timeline */}
        <Grid item xs={12} md={7}>
          <Card sx={{ borderRadius: 3, overflow: "hidden", minHeight: "75vh" }}>
            {!selectedSession ? (
              // İlk açılış: kullanıcı henüz session seçmedi
              <Box sx={{ p: 6, textAlign: "center", color: "text.secondary" }}>
                <VisibilityIcon sx={{ fontSize: 48, color: "#cbd5e1", mb: 2 }} />
                <Typography variant="h6" fontWeight={700}>Detay için session seçin</Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Sol listeden bir session'a tıklarsanız o ziyaretin sayfa-sayfa yolculuğunu görürsünüz.
                </Typography>
              </Box>
            ) : detailLoading ? (
              <Box sx={{ p: 6, textAlign: "center" }}><CircularProgress /></Box>
            ) : detail?.error ? (
              <Box sx={{ p: 3 }}><Alert severity="error">{detail.error}</Alert></Box>
            ) : detail ? (
              <Box>
                {/* Detay başlık */}
                <Box sx={{ p: 2, bgcolor: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                  <Typography variant="h6" fontWeight={700}>Yolculuk Timeline</Typography>
                  <Box sx={{ display: "flex", gap: 2, mt: 0.5, fontSize: 12, color: "text.secondary" }}>
                    <span><strong>{detail.total_pages}</strong> sayfa</span>
                    <span><strong>{detail.duration_minutes}</strong> dk süre</span>
                    <span style={{ fontFamily: "monospace", fontSize: 11 }}>{detail.session_id?.slice(0, 16)}...</span>
                  </Box>
                </Box>

                {/* Timeline — her sayfa bir kart */}
                <Box sx={{ p: 2, maxHeight: "65vh", overflow: "auto" }}>
                  {detail.pages.map((p, idx) => {
                    // Sayfa numarası göstergesi (sol kenarda numaralı dot)
                    const isFirst = idx === 0;
                    const isLast = idx === detail.pages.length - 1;
                    return (
                      <Box key={idx} sx={{ display: "flex", gap: 2, position: "relative", mb: 2 }}>
                        {/* Sol numaralı dot + dikey çizgi */}
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                          <Avatar sx={{
                            width: 28, height: 28, fontSize: 12, fontWeight: 800,
                            bgcolor: isFirst ? "#10b981" : isLast ? "#ef4444" : "#1565C0",
                            color: "white"
                          }}>
                            {idx + 1}
                          </Avatar>
                          {!isLast && <Box sx={{ flex: 1, width: 2, bgcolor: "#e2e8f0", my: 0.5, minHeight: 30 }} />}
                        </Box>
                        {/* Sağ kart: sayfa adı + meta */}
                        <Card variant="outlined" sx={{ flex: 1, p: 1.5, bgcolor: "#fafbfc" }}>
                          <Typography fontWeight={700} fontSize={13}>
                            {pageLabel(p.page)}
                          </Typography>
                          <Typography variant="caption" color="text.disabled" sx={{ display: "block", fontFamily: "monospace", fontSize: 10 }}>
                            {p.page}
                          </Typography>
                          <Box sx={{ display: "flex", gap: 1.5, mt: 0.5, alignItems: "center", flexWrap: "wrap" }}>
                            <Typography variant="caption" color="text.secondary">
                              {new Date(p.created_at).toLocaleString("tr-TR", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                            </Typography>
                            {p.dwell_seconds != null && (
                              <Chip size="small" label={`${fmtDuration(p.dwell_seconds)} kaldı`} sx={{ height: 18, fontSize: 10 }} />
                            )}
                            {isFirst && p.referrer && (
                              <Typography variant="caption" sx={{ color: "#3b82f6", fontSize: 10 }}>
                                Kaynak: {p.referrer.length > 40 ? p.referrer.slice(0, 40) + "..." : p.referrer}
                              </Typography>
                            )}
                          </Box>
                        </Card>
                      </Box>
                    );
                  })}

                  {/* Sipariş kartı — bu session'da sipariş varsa göster */}
                  {detail.orders && detail.orders.length > 0 && (
                    <Card sx={{ p: 2, mt: 2, bgcolor: "#f0fdf4", border: "2px solid #10b981" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                        <ShoppingCartIcon sx={{ color: "#10b981" }} />
                        <Typography fontWeight={800} sx={{ color: "#10b981" }}>
                          ✓ Bu yolculuk siparişle tamamlandı!
                        </Typography>
                      </Box>
                      {detail.orders.map(o => (
                        <Box key={o.id} sx={{ display: "flex", justifyContent: "space-between", py: 0.5 }}>
                          <Typography fontSize={13}>
                            <strong>#{o.id}</strong> — {o.status}
                          </Typography>
                          <Typography fontSize={13} fontWeight={700}>
                            {fmtMoney(o.total_price)}
                          </Typography>
                        </Box>
                      ))}
                    </Card>
                  )}
                </Box>
              </Box>
            ) : null}
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
